/**
 * Clustering helper for the location map — pure, so the clustering + grouping
 * logic is unit-testable without a live map.
 *
 * We cluster PER PERSONA (one Supercluster index each) so a cluster bubble is
 * always a single persona's colour/shape and never a muddy blend — and so the
 * persona filter toggles map cleanly to "which indexes we render."
 */
import Supercluster from 'supercluster';
import type { MapLocationPoint, MapPersonaKey } from '@truecare/schemas';

export type PointProps = MapLocationPoint & Record<string, unknown>;
export type PointFeature = GeoJSON.Feature<GeoJSON.Point, PointProps>;

export function toFeature(p: MapLocationPoint): PointFeature {
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
    properties: p as PointProps,
  };
}

export type PersonaIndexes = Map<MapPersonaKey, Supercluster<PointProps>>;

/** Build a Supercluster index per persona from the (already filtered) points. */
export function buildPersonaIndexes(points: MapLocationPoint[]): PersonaIndexes {
  const byPersona = new Map<MapPersonaKey, PointFeature[]>();
  for (const p of points) {
    const arr = byPersona.get(p.persona) ?? [];
    arr.push(toFeature(p));
    byPersona.set(p.persona, arr);
  }
  const indexes: PersonaIndexes = new Map();
  for (const [persona, features] of byPersona) {
    const index = new Supercluster<PointProps>({ radius: 60, maxZoom: 16 });
    index.load(features);
    indexes.set(persona, index);
  }
  return indexes;
}

/** [west, south, east, north] */
export type Bounds = [number, number, number, number];

export function clustersFor(
  index: Supercluster<PointProps>,
  bounds: Bounds,
  zoom: number,
): GeoJSON.Feature<GeoJSON.Point>[] {
  return index.getClusters(bounds, Math.round(zoom));
}

/** Apply the operator's active filters to the raw points (client-side, v1). A
 *  point is kept when its persona is enabled, its status is selected (or that
 *  persona has no status filter set), and — for caregivers — it has at least one
 *  of the selected roles (or no role filter set). */
export function filterPoints(
  points: MapLocationPoint[],
  filters: {
    personas: Set<MapPersonaKey>;
    statuses: Set<string>; // empty = all
    roles: Set<string>; // empty = all (caregiver only)
  },
): MapLocationPoint[] {
  return points.filter((p) => {
    if (!filters.personas.has(p.persona)) return false;
    if (filters.statuses.size > 0 && !filters.statuses.has(p.status)) return false;
    if (filters.roles.size > 0) {
      // Role filter only constrains caregivers; other personas have no roles, so
      // a role filter hides them (the operator is asking "show me these roles").
      if (p.roles.length === 0) return false;
      if (!p.roles.some((r) => filters.roles.has(r))) return false;
    }
    return true;
  });
}
